import { defineStore } from 'pinia'
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  HelpdeskClient,
  type Chat,
  type ChatStatus,
  type ConnectionState,
  type FeedbackDetail,
  type Message,
  type Subject,
  type Unsubscribe,
} from '@p-backoffice/helpdesk-sdk'
import { useUserStore } from '@/store/user'
import { decodeHtmlEntities } from '@/composables/utilities/general'

// The vendored SDK build (0.4.3) drops `edited_at` / `deleted_at` when it maps
// a REST message; helpdesk-sdk-ts v0.10.0 carries them as `editedAt` /
// `deletedAt`. Declare them optional here so the hydration paths already read
// them: against the current dist they are simply `undefined`, and they start
// working the moment the newer dist is vendored — no consumer change.
// Live edits/deletes don't depend on this at all; those arrive over the socket
// (see the `message_updated` / `message_deleted` handlers in connect()).
type ServerMessage = Message & {
  editedAt?: number | null
  deletedAt?: number | null
  // Bu store zaten bir kez öğrendi (bkz. `feedbackChoices`): SDK tipleri
  // camelCase iddia ediyor ama helpdesk API'si çalışma anında snake_case
  // gönderebiliyor. İki şekli de kabul et.
  edited_at?: number | string | null
  deleted_at?: number | string | null
}

/**
 * Sunucudan gelen "unix saniye" alanını güvenli bir sayıya indirger.
 *
 * Ham `== null` kontrolü yetmiyor, çünkü v0.10.0'ın bu YENİ ve NULLABLE
 * kolonları mevcut `mapMessage` üslubuyla (`new Date(m.created_at).getTime()`)
 * eşlemesi bütünüyle olası — o zaman hiç silinmemiş her mesaj `NaN` taşır.
 * `NaN == null` false olduğu için silinme filtresi TÜM thread'i eler ve
 * müşteri boş sohbet görür; `NaN != null` true olduğu için de her balona
 * "düzenlendi" basılır. Go tarafının `0` sıfır-değeri de aynı sonucu verir.
 * Bu yüzden yalnız POZİTİF ve SONLU bir sayı (ya da parse edilebilir bir
 * tarih dizesi) gerçek bir damga sayılır.
 */
function unixSeconds(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined
  }
  if (typeof value === 'string' && value !== '') {
    const ms = new Date(value).getTime()
    return Number.isFinite(ms) && ms > 0 ? ms / 1000 : undefined
  }
  return undefined
}

function messageEditedAt(msg: ServerMessage): number | undefined {
  return unixSeconds(msg.editedAt ?? msg.edited_at)
}

function messageDeletedAt(msg: ServerMessage): number | undefined {
  return unixSeconds(msg.deletedAt ?? msg.deleted_at)
}

export type PanelPhase =
  | 'idle'
  | 'guest-info'
  | 'subject-pick'
  | 'chat'
  | 'rating'
  | 'resolved'
  | 'closed'

export interface ViewAttachment {
  id: string | number
  type: 'image' | 'file'
  uri?: string
  fileName: string
  mimeType: string
  sizeBytes?: number
}

export interface ViewMessage {
  id: string
  text: string
  time: string // 'HH:mm' for inline display
  timestamp: number // unix seconds — used by consumers for date grouping
  // Unix seconds of the agent's last edit; undefined when never edited.
  // Drives the "düzenlendi" label beside the timestamp. The customer never
  // edits their own messages — this only ever reflects an agent's action.
  editedAt?: number
  type: 'user' | 'agent' | 'system'
  // Backend role — distinguishes AI bot from human agent. Optional because
  // synthesized system messages (e.g. participant_joined) don't carry one.
  authorRole?: 'bot' | 'user' | 'customer' | 'system'
  attachments?: ViewAttachment[]
  // `participant_joined` is synthesized from a WS event (agent name in
  // systemMeta). Other system breadcrumbs (e.g. the "Müşteri yeni chat
  // üzerinden döndü" message that BE appends to a merged thread) come
  // through with `type: 'system'` and no `systemKind` — their text is
  // BE-supplied and rendered as-is.
  systemKind?: 'participant_joined'
  systemMeta?: { name?: string }
}

export interface AttachmentBlobResult {
  blob: Blob | null
  // True when the download failed because the session is gone and the
  // store has already routed the user to re-auth. The caller must not also
  // show a generic "download failed" snackbar on top of that.
  tokenExpired: boolean
}

// Response headers must arrive within this budget.
const HEADER_TIMEOUT_MS = 15_000

// …after which the body is held to an *idle* budget instead: a transfer
// that keeps delivering bytes is never cut off however slow it is, but one
// that goes silent fails instead of hanging.
const BODY_IDLE_TIMEOUT_MS = 30_000

// Read the body chunk by chunk rather than via res.blob(), which offers no
// way to tell a slow transfer from a dead one.
async function readBlobWithIdleTimeout(res: Response, controller: AbortController): Promise<Blob> {
  const reader = res.body!.getReader()
  const chunks: BlobPart[] = []
  let idleTimer = setTimeout(() => controller.abort(), BODY_IDLE_TIMEOUT_MS)
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      // Progress resets the clock — the deadline is on silence, not on
      // total duration.
      clearTimeout(idleTimer)
      idleTimer = setTimeout(() => controller.abort(), BODY_IDLE_TIMEOUT_MS)
      if (value) chunks.push(value)
    }
  } finally {
    clearTimeout(idleTimer)
  }
  return new Blob(chunks, {
    type: res.headers.get('content-type') || 'application/octet-stream',
  })
}

function formatTime(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

function isImageMime(contentType: string): boolean {
  return typeof contentType === 'string' && contentType.startsWith('image/')
}

function statusToId(status: ChatStatus): number {
  // Map SDK status string → legacy numeric status_id used by PTicketList,
  // PHeader, AccountHover, and pages/index filters (`!== 4 && !== 5` for "open").
  switch (status) {
    case 'active':
      return 2
    case 'waiting':
      return 3
    case 'resolved':
      return 4
    case 'closed':
      return 5
    default:
      return 0
  }
}

// Map a ticket status to the panel phase, ignoring feedback flags. Two
// callers feed this: the live `ticket_updated` WS event (raw backend
// strings like 'open'/'pending') and loadChat (SDK-mapped ChatStatus like
// 'active'/'waiting'). Both vocabularies are handled so the derived phase
// is identical whether a status arrives over the socket or from getChat().
function statusToPhase(status: string): 'chat' | 'resolved' | 'closed' {
  // resolved = "Dönüş bekleniyor" (awaiting the customer's response) is a live
  // phase: keep the composer open so the user can still reply. The rating UI
  // belongs only to a fully closed (5) ticket, never to resolved.
  switch (status) {
    case 'new':
    case 'open':
    case 'bot_handling':
    case 'active':
    case 'pending':
    case 'waiting':
    case 'resolved':
      return 'chat'
    case 'closed':
      return 'closed'
    default:
      return 'chat'
  }
}

function toViewTicket(c: Chat) {
  return {
    id: c.id,
    subject: c.subject,
    status: c.status,
    status_id: statusToId(c.status),
    created_at: new Date(c.createdAt * 1000).toISOString(),
    last_message_at: new Date(c.lastMessageAt * 1000).toISOString(),
  }
}

function toViewMessage(msg: ServerMessage): ViewMessage {
  const type: ViewMessage['type'] =
    msg.authorType === 'customer' ? 'user' : msg.authorType === 'system' ? 'system' : 'agent'
  return {
    id: msg.id,
    // BE stores message bodies HTML-escaped, so a `>` arrives as `&gt;`.
    // Nothing renders a body as HTML — the bubbles print it through `{{ }}`
    // and the bot markdown re-escapes before parsing — so an entity left
    // encoded here is shown to the user verbatim. Decode on the way in,
    // once, for every path that displays a server message.
    text: decodeHtmlEntities(msg.body ?? ''),
    time: formatTime(msg.timestamp),
    timestamp: msg.timestamp,
    editedAt: messageEditedAt(msg),
    type,
    authorRole: msg.authorRole,
    attachments: (msg.attachments ?? []).map((a) => ({
      id: a.id,
      type: isImageMime(a.contentType) ? 'image' : 'file',
      uri: a.downloadUrl,
      fileName: a.fileName,
      mimeType: a.contentType,
      sizeBytes: a.sizeBytes,
    })),
  }
}

export const useHelpdeskChatStore = defineStore('helpdeskChat', {
  state: () => ({
    client: null as HelpdeskClient | null,
    unsubs: [] as Unsubscribe[],

    isPanelOpen: false,

    chatId: null as string | null,
    phase: 'idle' as PanelPhase,
    connectionState: 'disconnected' as ConnectionState,
    prevConnectionState: 'disconnected' as ConnectionState,
    // True after the initial loadChat of this session has succeeded.
    // The onConnectionChange handler uses this to gate re-pulls so we
    // don't double-fetch on the very first 'connected' transition (which
    // is already covered by the explicit loadChat call inside connect()).
    hasInitiallyLoaded: false,

    // Guest session token (sess_*) returned by HelpdeskClient.createGuestSession.
    // Held in memory only — refreshing the tab drops the guest session, by design.
    guestSessionToken: null as string | null,

    // Cached locale. We can't call `useI18n()` from within an async store
    // action after an `await` (the Nuxt setup context is lost), so the
    // locale watcher seeds this on mount and keeps it updated.
    currentLocale: 'tr' as string,

    // Quick-solution shortcut (e.g. home page "Hızlı çözümler" card).
    // Set by requestQuickSolution before opening the panel; consumed by
    // connect() once the client is ready, skipping subject-pick.
    // We carry the localized title as both `firstMessage` and a hint
    // for matching against subjects.name (mirrors samaritan, which uses
    // names to avoid hard-coding env-specific subject IDs).
    pendingSubject: null as { firstMessage: string; subjectNameHint: string } | null,

    messages: [] as ViewMessage[],
    // Socket üzerinden görülen silme ve düzenlemelerin hafızası — SOHBET
    // BAŞINA anahtarlanmış. Reconnect re-pull'u (onConnectionChange → loadChat)
    // thread'i API'den baştan çekiyor; hafıza olmasa müşterinin gözünün önünde
    // kaybolan mesaj geri geliyor, beliren "düzenlendi" işareti siliniyor ve
    // uçuşta olan bir `getChat` düzenleme ÖNCESİ gövdeyi geri yazıyordu.
    //
    // Anahtar sohbet id'si: düz bir liste "silme hafızası thread'e özel"
    // iddiasını üç ayrı sıfırlama noktasına bağlıyordu ve `loadChat`in
    // client'sız dalı ile duplicate-merge dalı o noktaların dışında kalıyordu.
    // Oturum kapsamlı: tam sayfa yenilemesinden sonra kaynak API'nin kendisi.
    _deletedByChat: {} as Record<string, string[]>,
    _editsByChat: {} as Record<string, Record<string, { text: string; editedAt: number }>>,
    isTyping: false,
    typingTimeout: null as ReturnType<typeof setTimeout> | null,

    // Public list shown in the picker — catchall row stripped out.
    subjects: [] as Subject[],
    // Internal full list (incl. catchall) used by resolveSubjectId() to
    // pick the implicit fallback when the user hasn't selected a subject.
    _allSubjects: [] as Subject[],

    // Last ticket id we subscribed to over WS via the SDK. Tracked so a
    // subsequent startNewChat (incl. the redirect-into-different-thread
    // edge case) can unsubscribe before re-subscribing — keeps inbound
    // events from leaking across chats.
    _subscribedChatId: null as string | null,

    rating: 0,
    feedbackDetail: null as FeedbackDetail | null,
    // True once the customer has submitted feedback for the CURRENT close
    // cycle. Suppresses a repeat rating screen if another feedback_requested
    // event fires for the same closed ticket. Reset on reopen and on chat
    // switch, so a genuine reopen→reclose still triggers a fresh survey.
    feedbackSubmitted: false,

    error: null as string | null,
    loading: false,

    attachmentViewUrls: {} as Record<string, string>,

    // Ticket list (SDK-backed) — exposed to /ticket page, /index, PHeader, AccountHover.
    // Shape mirrors the legacy ticketStore.tickets[] for backward compatibility.
    tickets: [] as Array<{
      id: string
      subject: string
      status: ChatStatus
      status_id: number
      created_at: string
      last_message_at: string
    }>,
    ticketsTotal: 0,
    // Server-side count of open tickets across ALL pages (from the API's
    // `open_total`). Stable: unaffected by which page is loaded or which tab
    // is active. Used as the badge floor so the count doesn't fluctuate with
    // pagination/tab-switching.
    ticketsOpenTotal: 0,
    ticketsPage: 1,
    ticketsPerPage: 25,
    ticketsFetched: false,
  }),

  getters: {
    isClosed: (s): boolean => s.phase === 'closed' || s.phase === 'resolved',
    needsSubject: (s): boolean => s.phase === 'subject-pick',

    // Active-tab badge count. Two signals are combined with max():
    //  • ticketsOpenTotal — the server's open_total across ALL pages. Stable,
    //    never fluctuates with pagination or tab-switching, but may exclude
    //    resolved (4) tickets depending on the backend.
    //  • loaded non-closed count — everything not closed (5) in the currently
    //    loaded list, which DOES include resolved tickets.
    // max() keeps the badge stable (>= server floor) while guaranteeing it is
    // never lower than what the user actually sees in the list (>= loaded
    // non-closed), so the badge can't undercount the visible active tab.
    openTicketsCount: (s): number =>
      Math.max(s.ticketsOpenTotal, s.tickets.filter((t) => t.status_id !== 5).length),

    // Id of the implicit-fallback subject (BE flags exactly one row with
    // is_catchall=true). Returns null when the list isn't loaded yet or
    // the env doesn't have one (e.g. during a partial deploy) — callers
    // route to subject-pick in that case.
    catchallSubjectId: (s): number | null => {
      const fallback = s._allSubjects.find((sub) => sub.is_catchall)
      return fallback?.id ?? null
    },

    // Sorted list of feedback choices for the current rating, ready for
    // PFeedbackForm. Empty when no rating yet, no feedbackDetail, or the
    // server returns no choices for that rating.
    //
    // The SDK types claim camelCase (`choicesByRating`, `choiceText`,
    // `displayOrder`) but the helpdesk API ships snake_case at runtime —
    // accept both shapes defensively.
    feedbackChoices: (s): Array<{ id: number; text: string }> => {
      if (!s.rating || !s.feedbackDetail) return []
      const detail = s.feedbackDetail as unknown as Record<string, unknown>
      const map =
        (detail.choicesByRating as Record<string, unknown[]> | undefined) ??
        (detail.choices_by_rating as Record<string, unknown[]> | undefined) ??
        {}
      const list = (map[s.rating.toString()] ?? []) as Array<Record<string, unknown>>

      return [...list]
        .sort(
          (a, b) =>
            ((a.displayOrder as number | undefined) ?? (a.display_order as number) ?? 0) -
            ((b.displayOrder as number | undefined) ?? (b.display_order as number) ?? 0),
        )
        .map((c) => ({
          id: c.id as number,
          text: (
            (c.choiceText as string | undefined) ??
            (c.choice_text as string) ??
            ''
          ).toString(),
        }))
    },
  },

  actions: {
    // Visibility (replaces composables/useChat.ts) -----------------------
    openPanel() {
      this.isPanelOpen = true
    },
    closePanel() {
      this.isPanelOpen = false
      // Samaritan parity: in the mobile app the chat is a routed screen,
      // so closing it unmounts useHelpdeskChat — destroyHelpdeskClient()
      // tears down the SDK and the hook's in-memory state (guest token,
      // messages, needsGuestInfo) dies with the component. Re-entering
      // the screen always starts fresh.
      // Our PChatContainer is layout-persistent, so panel close ≠ unmount.
      // Replicate samaritan's effect for guests only: drop the SDK +
      // chat state so the next FAB open shows the guest-info form again.
      // Authenticated users keep their conversation across panel toggles.
      if (this.guestSessionToken) {
        this.guestSessionToken = null
        this.pendingSubject = null
        this.disconnect()
        this.resetForNewChat()
      }
      // Refresh the tickets list so /index and /ticket reflect any
      // server-side status changes that happened during the conversation
      // (resolved/closed by agent). Skip if the list was never fetched.
      if (this.ticketsFetched) {
        this.loadTickets().catch(() => {})
      }
    },
    togglePanel() {
      this.isPanelOpen = !this.isPanelOpen
    },

    // Resets the in-flight chat selection. Called by the floating-button
    // handler so opening the FAB always lands on subject-pick. Existing
    // tickets are reached from /ticket via loadChat instead.
    resetForNewChat() {
      this.chatId = null
      this.messages = []
      this.phase = 'idle'
      this.isTyping = false
      this.feedbackDetail = null
      this.rating = 0
      this.feedbackSubmitted = false
      // Drop any error from the prior session — otherwise stale messages
      // like 'feedback_failed' bleed into the next chat (visible in the
      // guest-info form, subject picker, etc.).
      this.error = null
    },

    // Quick-solution shortcut: open the panel and start a new chat
    // for the given title, skipping subject-pick.
    //
    // Samaritan parity: each new chat session is bracketed by a full
    // SDK teardown + reconnect — the mobile app gets this for free
    // because the chat screen unmounts/remounts. We replicate the
    // effect here so the previous chat's WS channel doesn't bleed
    // its events into the next session. connect() consumes
    // pendingSubject (or routes to guest-info when there's no token).
    async requestQuickSolution(title: string, subjectName?: string): Promise<void> {
      this.resetForNewChat()
      this.openPanel()
      // subjectName must match Subject.name returned by /tickets/subjects
      // exactly — _startQuickChat uses it to look up the id. When omitted
      // we fall back to the title (legacy callers) which usually won't
      // match and lands the user on subjects[0].
      this.pendingSubject = { firstMessage: title, subjectNameHint: subjectName ?? title }
      if (this.client) this.disconnect()
      await this.connect()
    },

    // Resolve a subject by name (samaritan-style — robust to env-specific
    // IDs) and kick off the chat. When the hint doesn't match a visible
    // subject, fall through to the catchall row (BE-marked implicit
    // fallback). If neither matches and there's no catchall in this env,
    // drop to subject-pick — BE would otherwise reject with 400.
    async _startQuickChat(firstMessage: string, subjectNameHint: string): Promise<void> {
      if (this._allSubjects.length === 0) await this.loadSubjects()
      const subjectId = this.resolveSubjectId(null, subjectNameHint)
      if (!subjectId) {
        this.phase = 'subject-pick'
        return
      }
      await this.startNewChat(subjectId, firstMessage)
    },

    // FAB → fresh subject-pick: clear in-memory chat state and recycle
    // the SDK so the previous chat's WS channel (subscribed by an
    // earlier startNewChat/loadChat) is dropped before subjects load.
    //
    // Samaritan parity: equivalent to the chat screen unmounting (which
    // calls destroyHelpdeskClient()) and remounting (which runs
    // connect() afresh). Without this, subscribedChannels accumulates
    // for the whole session and inbound events from old chats appear
    // in the new one.
    //
    // connect() handles the no-token case by setting phase='guest-info'
    // itself, and lands on subject-pick when there's no chatId.
    async startNewSubjectPick(): Promise<void> {
      this.resetForNewChat()
      if (this.client) this.disconnect()
      await this.connect()
    },

    // Bütün toplu mesaj atamalarının ortak yolu (loadChat + startNewChat'in
    // duplicate-merge dalı): silinenleri ele, socket'te görülen düzenlemeleri
    // yeniden uygula, eskiden yeniye sırala, view şekline çevir.
    _hydrateMessages(list: ServerMessage[]): ViewMessage[] {
      const deleted = this._deletedIds()
      const edits = this._chatEdits()
      return [...list]
        .filter((m) => messageDeletedAt(m) === undefined && !deleted.includes(m.id))
        .sort((a, b) => a.timestamp - b.timestamp)
        .map((m) => {
          const view = toViewMessage(m)
          const seen = edits[view.id]
          // Sunucu en az bizim kadar taze bir düzenleme bildiriyorsa o kazanır;
          // aksi hâlde (vendored 0.4.3 `edited_at`i hiç taşımıyor, ya da yanıt
          // düzenlemeden önce yola çıkmış) socket'te gördüğümüzü geri koyarız.
          if (seen && (view.editedAt === undefined || view.editedAt < seen.editedAt)) {
            view.text = seen.text
            view.editedAt = seen.editedAt
          }
          return view
        })
    },

    _deletedIds(): string[] {
      return this.chatId ? (this._deletedByChat[this.chatId] ?? []) : []
    },

    _chatEdits(): Record<string, { text: string; editedAt: number }> {
      return this.chatId ? (this._editsByChat[this.chatId] ?? {}) : {}
    },

    _rememberDeletion(messageId: string): void {
      if (!this.chatId) return
      const ids = this._deletedByChat[this.chatId] ?? []
      if (!ids.includes(messageId)) this._deletedByChat[this.chatId] = [...ids, messageId]
    },

    _rememberEdit(messageId: string, text: string, editedAt: number): void {
      if (!this.chatId) return
      this._editsByChat[this.chatId] = {
        ...(this._editsByChat[this.chatId] ?? {}),
        [messageId]: { text, editedAt },
      }
    },

    /**
     * WS payload'ı AÇIK sohbete mi ait?
     *
     * `ticket_id` yoksa SDK'nın her payload'a damgaladığı `_channel`
     * (`ticket:<id>`) ikinci kaynak olarak okunur — abonelik listesi birden
     * fazla ticket kanalı tutabildiği için "id yoksa açık sohbettir" varsayımı
     * gerçekten son çare olmalı.
     */
    _isOpenChatEvent(
      ticketId: number | string | null | undefined,
      channel?: string | null,
    ): boolean {
      const fromChannel = typeof channel === 'string' ? channel.split(':')[1] : undefined
      const id = ticketId ?? fromChannel
      if (id == null || id === '') return true
      return this.chatId != null && String(this.chatId) === String(id)
    },

    // Internal helper used by toViewMessage callers
    _appendMessage(vm: ViewMessage) {
      // A message the agent deleted must not come back through any append
      // path — including the branch of onAttachmentAdded that synthesizes a
      // bubble for a message it can't find in the thread.
      if (this._deletedIds().includes(vm.id)) return
      if (this.messages.some((m) => m.id === vm.id)) return
      this.messages.push(vm)
    },

    // Bootstrap --------------------------------------------------------

    // Customer token the SDK authenticates with, in priority order:
    //   1) guest session token (in-memory, set by connectAsGuest)
    //   2) authenticated user token (Pinia / localStorage)
    // Shared by ensureClient and by fetchAttachmentBlob, which relays this
    // token to the Nitro download proxy.
    _resolveAuthToken(): string | null {
      // Lazy reads — only safe in the browser.
      if (typeof window === 'undefined') return null

      const userStore = useUserStore()
      let token: string | null = this.guestSessionToken
      if (!token) token = userStore.user.token
      if (!token) {
        try {
          token = localStorage.getItem('auth-token')
        } catch {
          token = null
        }
      }
      return token
    },

    ensureClient(): HelpdeskClient | null {
      // Pinia stores the class instance as a structural subset of its
      // public surface, so TS no longer recognises it as the class type
      // even though the runtime object IS the original instance. The
      // casts here are safe — the SDK is constructed below.
      if (this.client) return this.client as unknown as HelpdeskClient

      // Lazy reads — only safe in the browser.
      if (typeof window === 'undefined') return null

      const config = useRuntimeConfig()
      const token = this._resolveAuthToken()

      // No token at all → caller (connect / startNewSubjectPick) routes
      // the panel to phase='guest-info' so the user can fill the form.
      if (!token) return null

      const apiUrl = config.public.helpdeskBaseUrl as string
      const wsUrl = config.public.wsUrl as string
      const locale = this.currentLocale

      this.client = new HelpdeskClient({
        apiUrl,
        wsUrl,
        authToken: token,
        locale,
        useWsTicket: true,
        reconnect: true,
        reconnectMaxRetries: 5,
      })

      return this.client as unknown as HelpdeskClient
    },

    async connect(): Promise<void> {
      const client = this.ensureClient()
      if (!client) {
        // No auth token and no guest session yet — show the guest info
        // form. Submitting it calls connectAsGuest() which mints a
        // session token and re-enters this action.
        this.phase = 'guest-info'
        return
      }

      // Idempotent: clear any prior subscriptions before re-subscribing.
      this.unsubs.forEach((off) => off())
      this.unsubs = []

      this.unsubs.push(
        client.onMessage(async (msg) => {
          // Skip own optimistic copy
          if (msg.authorType === 'customer') return
          this.clearTypingFallback()
          this.isTyping = false
          this._appendMessage(toViewMessage(msg))
          // Resolve presigned URLs for image attachments
          for (const att of msg.attachments ?? []) {
            const isImage =
              typeof att.contentType === 'string' && att.contentType.startsWith('image/')
            if (!isImage) continue
            try {
              const url = await client.getAttachmentViewUrl(att.id)
              this.attachmentViewUrls[att.id] = url
              const target = this.messages.find((m) => m.id === msg.id)
              const targetAtt = target?.attachments?.find((a) => a.id === att.id)
              if (targetAtt) targetAtt.uri = url
            } catch {
              /* swallow — UI will show "Loading..." until next attempt */
            }
          }
        }),
      )

      this.unsubs.push(
        client.onTyping((e) => {
          this.clearTypingFallback()
          this.isTyping = e.isTyping
          if (e.isTyping) {
            this.typingTimeout = setTimeout(() => {
              this.isTyping = false
              this.typingTimeout = null
            }, 5000)
          }
        }),
      )

      this.unsubs.push(
        // Live status changes arrive as `ticket_updated` events, NOT through
        // the SDK's onStatusChange/"status" channel — this backend never
        // emits the latter. The SDK keys WS events by `r.type`, and a status
        // change ships as `r.type === 'ticket_updated'` with the new status
        // nested under `r.ticket.status` (raw strings like 'open'/'closed').
        // Subscribing to onStatusChange here would be dead code, which is
        // why a reopened ticket only "unstuck" after a page refresh
        // (loadChat → getChat) before. Listen to the real event instead.
        client.on('ticket_updated', (payload: unknown) => {
          const data = payload as
            | {
                ticket?: { id?: number | string; status?: string; status_id?: number }
                _channel?: string
              }
            | undefined
          const ticket = data?.ticket
          const status = ticket?.status
          if (!status) return

          // Reflect the new status in the cached list so /ticket and the badge
          // update live — in place (no refetch, no loading flicker, no
          // page-collapse). Keyed by the event's ticket id, not the open chat.
          if (ticket?.id != null && typeof ticket.status_id === 'number') {
            const cached = this.tickets.find((x) => String(x.id) === String(ticket.id))
            if (cached) cached.status_id = ticket.status_id
          }

          // Phase tracks only the currently-open chat.
          if (!this._isOpenChatEvent(ticket?.id, data?._channel)) return

          const next = statusToPhase(status)
          // Reopen always wins: when the agent moves a closed/rated ticket
          // back to an open status, restore the composer (phase==='chat')
          // even if the rating UI or thanks panel is currently showing.
          if (next === 'chat') {
            this.phase = 'chat'
            // New live cycle — clear the "already rated" guard so a later
            // reclose can request fresh feedback (separate survey).
            this.feedbackSubmitted = false
            return
          }
          // Closed (5): don't trust the WS feedback event for the phase. Show
          // the closed banner immediately, then pull the ticket's real
          // feedback flags from the API and let those decide whether the
          // rating screen appears (only if requested and not yet rated).
          this.phase = 'closed'
          this.syncFeedbackState().catch(() => {})
        }),
      )

      this.unsubs.push(
        client.onFeedbackRequested(() => {
          // The feedback_triggered event just flipped the flags — re-read the
          // ticket from the API and let its real feedback_requested /
          // feedback_submitted decide. syncFeedbackState only shows the rating
          // for a closed (5), not-yet-rated ticket, so a resolved ticket's
          // re-emitted event (on WS subscribe) and an already-rated ticket
          // both stay quiet.
          this.syncFeedbackState().catch(() => {})
        }),
      )

      this.unsubs.push(
        client.onAttachmentAdded(async (e) => {
          // Skip own optimistic copy
          if (e.authorType === 'customer') return
          const target = this.messages.find((m) => m.id === e.messageId)
          const att = e.attachment
          const isImage =
            typeof att.contentType === 'string' && att.contentType.startsWith('image/')
          const view: ViewAttachment = {
            id: att.id,
            type: isImage ? 'image' : 'file',
            uri: att.downloadUrl,
            fileName: att.fileName,
            mimeType: att.contentType,
            sizeBytes: att.sizeBytes,
          }
          if (target) {
            const list = target.attachments ?? []
            if (!list.some((a) => a.id === att.id)) {
              target.attachments = [...list, view]
            }
          } else {
            // Attachment arrived before/without a base message — synthesize a
            // standalone agent message so it still renders.
            this._appendMessage({
              id: e.messageId,
              text: '',
              time: formatTime(e.timestamp),
              timestamp: e.timestamp,
              type: 'agent',
              attachments: [view],
            })
          }
          // Resolve presigned URL for images.
          if (isImage) {
            try {
              const url = await client.getAttachmentViewUrl(att.id)
              this.attachmentViewUrls[att.id] = url
              const t = this.messages.find((m) => m.id === e.messageId)
              const a = t?.attachments?.find((x) => x.id === att.id)
              if (a) a.uri = url
            } catch {
              /* skip */
            }
          }
        }),
      )

      this.unsubs.push(
        client.on('participant_joined', (payload: unknown) => {
          const data = payload as { name?: string; timestamp?: number } | undefined
          const name = data?.name?.trim()
          if (!name) return
          const ts =
            typeof data?.timestamp === 'number' ? data.timestamp : Math.floor(Date.now() / 1000)
          const id = `system-join-${ts}-${name}`
          this._appendMessage({
            id,
            text: '',
            time: formatTime(ts),
            timestamp: ts,
            type: 'system',
            systemKind: 'participant_joined',
            systemMeta: { name },
          })
        }),
      )

      // An agent editing or deleting a message it already sent. Neither event
      // is typed by the vendored SDK, so both come through the generic `on()`
      // channel like `ticket_updated` above. The customer hub payload is
      // deliberately minimal — no `edited_by` / `old_body` / `deleted_by`, and
      // internal notes never reach it at all — so there is nothing here to
      // attribute to a particular agent.
      //
      // There is no edit/delete affordance on this side by product decision:
      // the customer cannot touch their own messages, they only ever watch
      // what an agent did to the thread.
      this.unsubs.push(
        client.on('message_updated', (payload: unknown) => {
          const data = payload as
            | {
                ticket_id?: number | string
                message_id?: number | string
                body?: string
                edited_at?: number
                _channel?: string
              }
            | undefined
          if (!this._isOpenChatEvent(data?.ticket_id, data?._channel)) return
          if (data?.message_id == null) return
          const targetId = String(data.message_id)
          const target = this.messages.find((m) => m.id === targetId)
          // Not in the thread we have rendered (never loaded, or already
          // deleted) — nothing to patch.
          if (!target) return

          const editedAt = unixSeconds(data.edited_at)
          const hasBody = typeof data.body === 'string'
          // İşaret yalnız GERÇEK bir düzenleme kanıtıyla basılır. Ne gövde ne
          // `edited_at` taşıyan bir `message_updated` (okundu bilgisi, ek
          // sayacı, resubscribe tekrarı, mükerrer teslim) mesajı düzenlenmiş
          // göstermemeli — işaret bir daha hiç kalkmıyor.
          if (!editedAt && !hasBody) return

          // Same decode as every other inbound body: BE stores them
          // HTML-escaped, so a `>` arrives as `&gt;` (see toViewMessage).
          if (hasBody) target.text = decodeHtmlEntities(data.body as string)
          // `time`/`timestamp` deliberately keep the original send time — an
          // edit must not reorder the thread or move the message's date group.
          target.editedAt = editedAt ?? Math.floor(Date.now() / 1000)
          this._rememberEdit(targetId, target.text, target.editedAt)
        }),
      )

      this.unsubs.push(
        client.on('message_deleted', (payload: unknown) => {
          const data = payload as
            | { ticket_id?: number | string; message_id?: number | string; _channel?: string }
            | undefined
          if (!this._isOpenChatEvent(data?.ticket_id, data?._channel)) return
          if (data?.message_id == null) return
          const deletedId = String(data.message_id)
          const removed = this.messages.find((m) => m.id === deletedId)
          this.messages = this.messages.filter((m) => m.id !== deletedId)
          // Balon gitti; presigned URL'leri artık var olmayan içeriğe ait.
          for (const att of removed?.attachments ?? []) {
            delete this.attachmentViewUrls[String(att.id)]
          }
          // Remember it — a reconnect re-pull would otherwise bring it back.
          this._rememberDeletion(deletedId)
        }),
      )

      this.unsubs.push(
        client.onConnectionChange((state) => {
          const prev = this.prevConnectionState
          this.prevConnectionState = state
          this.connectionState = state
          if (
            state === 'connected' &&
            prev !== 'connected' &&
            this.chatId &&
            this.hasInitiallyLoaded
          ) {
            // Re-pull history after a reconnect to catch missed messages.
            // Guarded by `hasInitiallyLoaded` so the very first connect
            // doesn't double-fetch — the explicit loadChat below in
            // connect() already covers the initial load.
            this.loadChat(this.chatId).catch(() => {})
          }
        }),
      )

      try {
        await client.connect()
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'connect_failed'
        return
      }

      // No persistence: connect lands on subject-pick unless a chatId is
      // already in memory (set by /ticket → loadChat before opening the
      // panel) or a pendingSubject is queued by the quick-solution flow.
      if (this.chatId) {
        await this.loadChat(this.chatId)
        this.hasInitiallyLoaded = true
      } else if (this.pendingSubject) {
        const { firstMessage, subjectNameHint } = this.pendingSubject
        this.pendingSubject = null
        await this._startQuickChat(firstMessage, subjectNameHint)
      } else {
        await this.loadSubjects()
        this.phase = 'subject-pick'
      }
    },

    clearTypingFallback() {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout)
        this.typingTimeout = null
      }
    },

    // Anonymous flow: mint a sess_* token via the SDK's static helper,
    // store it in memory, then run the regular connect path. ensureClient
    // picks the token off `guestSessionToken` and constructs the client
    // with `useWsTicket: true`.
    // `phone` is the national part without the trunk zero (e.g. `5346536990`)
    // and `tckn` is optional — both are dropped from the session request body
    // by the SDK when empty.
    async connectAsGuest(name: string, email: string, phone: string, tckn?: string): Promise<void> {
      this.error = null
      this.loading = true
      try {
        const config = useRuntimeConfig()
        const apiUrl = config.public.helpdeskBaseUrl as string
        const sessionToken = await HelpdeskClient.createGuestSession(
          apiUrl,
          name,
          email,
          phone,
          tckn,
        )
        this.guestSessionToken = sessionToken
        await this.connect()
        // Connect ends on subject-pick for a fresh guest session.
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'guest_session_failed'
        this.phase = 'guest-info'
      } finally {
        this.loading = false
      }
    },

    // Chat lifecycle ---------------------------------------------------
    async loadSubjects(): Promise<void> {
      const client = this.client
      if (!client) return
      this.loading = true
      try {
        const all = await client.getSubjects()
        this._allSubjects = all
        // Strip the catchall row from the picker — it's an implicit
        // fallback, never a user-selectable category.
        this.subjects = all.filter((s) => !s.is_catchall)
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'subjects_failed'
      } finally {
        this.loading = false
      }
    },

    // Resolve which subject id to send when starting a chat:
    //   1) `picked` wins when the user explicitly selected a category.
    //   2) Otherwise try to match `hint` against the user-visible subject
    //      names (samaritan-style env-portable lookup for quick-solution
    //      tiles whose names mirror Subject.name).
    //   3) Otherwise fall to the catchall id (implicit fallback for the
    //      "Düz yaz" / direct-message flow).
    //   4) Returns null when none of the above resolve — caller should
    //      route to subject-pick (BE would otherwise reject with 400
    //      SUBJECT_REQUIRED on a partial deploy without a catchall row).
    resolveSubjectId(picked: number | null, hint?: string): number | null {
      if (picked) return picked
      if (hint) {
        const match = this._allSubjects.find((s) => s.name === hint && !s.is_catchall)
        if (match) return match.id
      }
      return this.catchallSubjectId
    },

    // Used by /index quick-solution tiles to populate the subject list
    // before the chat panel mounts. ensureClient() builds the SDK with
    // any available token; getSubjects() is a plain HTTP GET so no WS
    // connect is required. No-op for guests with no token.
    async ensureSubjectsLoaded(): Promise<void> {
      if (this.subjects.length > 0) return
      if (!this.ensureClient()) return
      await this.loadSubjects()
    },

    async startNewChat(subjectId: number | null, firstMessage: string): Promise<void> {
      const client = this.client
      if (!client) return

      // null/0 = "no subject picked" — fall to the catchall row. This is
      // the path used by the direct-input flow (user types straight into
      // the chat bar without choosing a category) and any future callers
      // that want the implicit fallback. Picker-driven calls always pass
      // a real id and bypass this branch.
      const resolvedSubjectId = subjectId || this.catchallSubjectId
      if (!resolvedSubjectId) {
        // No catchall configured AND no picked subject — BE would reject
        // with 400 SUBJECT_REQUIRED. Surface the picker so the user has
        // to choose explicitly.
        this.error = 'subject_required'
        this.phase = 'subject-pick'
        return
      }

      this.loading = true
      try {
        const result = await client.startNewChat(resolvedSubjectId, firstMessage)
        const newChatId = result.chat.id

        // If a prior ticket was subscribed (rare — usually startNewChat is
        // called from a clean state, but the BE-driven duplicate-merge can
        // route us into a different existing thread), drop the old WS
        // subscription before binding to the new one so events from the
        // previous chat don't bleed into the rendered thread.
        if (this._subscribedChatId && this._subscribedChatId !== newChatId) {
          try {
            client.unsubscribeFromChat(this._subscribedChatId)
          } catch {
            /* best-effort */
          }
        }
        this._subscribedChatId = newChatId
        this.chatId = newChatId

        // Mark the session as initialised so a WS drop during this fresh
        // chat triggers the reconnect re-pull (onConnectionChange gates on
        // hasInitiallyLoaded). Without this, messages received during an
        // outage on a brand-new chat are silently lost.
        this.hasInitiallyLoaded = true

        if (result.redirected) {
          // Duplicate-merge path: BE attached our message to an existing
          // active thread for the same subject.
          let messagesSource = result.messages
          if (!messagesSource || messagesSource.length === 0) {
            // BE flagged the merge but didn't include the thread history.
            // Fetch it explicitly so the user lands on the existing chat
            // with full context instead of an empty pane. (Defensive — the
            // contract is for `messages` to be populated when redirected,
            // but a partial deploy / proxy might strip it.)
            try {
              const detail = await client.getChat(newChatId)
              messagesSource = detail.messages
            } catch {
              messagesSource = []
            }
          }
          this.messages = this._hydrateMessages(messagesSource)
          // BE includes its own "Müşteri yeni chat üzerinden döndü"
          // system breadcrumb in the merged thread (delivered via the
          // messages array here and/or pushed over WS), so we don't
          // synthesize an extra one client-side — that would render the
          // notice twice.
          this.isTyping = false
        } else {
          // Normal new-ticket path: optimistic local first message — the
          // agent reply will arrive over WS.
          const localId = `local-${Date.now()}`
          const nowSec = Math.floor(Date.now() / 1000)
          this.messages = [
            {
              id: localId,
              text: firstMessage,
              time: formatTime(nowSec),
              timestamp: nowSec,
              type: 'user',
            },
          ]
          this.isTyping = true
          // `StartChatResult` yeni mesajın id'sini taşımıyor, dolayısıyla bu
          // balon `local-…` üstünde kalırdı — sendMessage'daki notun aynısı:
          // edit_any / delete_any taşıyan bir ajanın YENİ talebin İLK mesajına
          // dokunması burada görünmez olurdu, ki cevapladığı mesaj tam olarak
          // o. Optimistic render anında kalsın diye arka planda uzlaştırılıyor;
          // thread'i baştan yazmıyoruz, yalnız id'yi benimsiyoruz — aradaki bir
          // WS mesajı böylece kaybolmaz.
          client
            .getChat(newChatId)
            .then((detail) => {
              const first = [...detail.messages]
                .filter((m) => m.authorType === 'customer')
                .sort((a, b) => a.timestamp - b.timestamp)[0]
              const target = this.messages.find((m) => m.id === localId)
              if (first && target) target.id = first.id
            })
            .catch(() => {
              /* best-effort — balon `local-…` kalır, eski davranış */
            })
        }
        this.phase = 'chat'
      } catch (e) {
        // BE returns 400 SUBJECT_REQUIRED when neither a picked subject
        // nor a catchall row is available — route the user back to the
        // picker so they can choose explicitly.
        const code = (e as { code?: string } | null)?.code
        if (code === 'SUBJECT_REQUIRED') {
          this.error = 'subject_required'
          this.phase = 'subject-pick'
          return
        }
        this.error = e instanceof Error ? e.message : 'start_chat_failed'
      } finally {
        this.loading = false
      }
    },

    async loadChat(existingChatId: string): Promise<void> {
      // Switching to a different ticket — recycle the SDK so the
      // previous ticket's WS channel is dropped before the new one is
      // subscribed inside getChat(). Samaritan parity: same effect as
      // unmounting the chat screen and remounting with a different id.
      // connect() will re-enter this action recursively with a live
      // client (no-switch branch) since chatId is now in memory.
      if (this.client && this.chatId && this.chatId !== existingChatId) {
        this.disconnect()
        this.chatId = existingChatId
        await this.connect()
        return
      }

      // Coalesce duplicate in-flight loads for the same chatId — rapid
      // clicks on the same ticket would otherwise fire parallel getChat
      // calls and race on phase/messages assignment. Scoped to the same
      // id so a concurrent loadTickets/endChat (which also flip
      // `loading`) doesn't drop a legitimate ticket switch.
      if (this.loading && this.chatId === existingChatId) return

      // Set the selection up front. If the client isn't connected yet
      // (e.g. user clicked a ticket while the panel was closed), the
      // panel-open watcher will run connect() and re-enter this action
      // with a live client.
      this.chatId = existingChatId

      const client = this.client
      if (!client) return
      this.loading = true
      try {
        const detail = await client.getChat(existingChatId)
        this.chatId = detail.id

        // Always reset rating before applying loaded feedback state —
        // switching from a submitted chat (rating=3) to a pending one
        // would otherwise render the feedback-form takeover, since the
        // template branches on `phase==='rating' && rating>0`.
        this.rating = 0
        this.feedbackDetail = null
        // Sync the already-rated guard with server truth so a re-emitted
        // feedback_requested can't re-open the modal ("daha önce
        // değerlendirdiyse çıkmamalı").
        this.feedbackSubmitted = !!detail.feedbackSubmitted

        if (
          detail.status === 'active' ||
          detail.status === 'waiting' ||
          detail.status === 'resolved'
        ) {
          // active/waiting/resolved are all live phases — keep the composer.
          // resolved ("Dönüş bekleniyor") means the agent is waiting for the
          // customer's reply, so the user MUST be able to type. The feedback
          // flags from an earlier round may still be set on a resolved ticket;
          // checking them first would strand it on 'rating' and show the
          // evaluation screen with the composer hidden. Rating only belongs to
          // a fully closed (5) ticket, handled below.
          // Reopen relies on this too: an agent moving a closed ticket back to
          // open/resolved restores the composer here on the next load.
          this.phase = 'chat'
        } else if (detail.feedbackSubmitted) {
          // Customer already rated — show the resolved/thanks panel.
          // No need to fetch the feedback row (no choices to render).
          this.phase = 'resolved'
        } else if (detail.feedbackRequested) {
          // Agent asked for feedback but it isn't submitted yet — fetch
          // the choices so the form can render once the user picks a star.
          this.phase = 'rating'
          try {
            this.feedbackDetail = await client.getFeedback(detail.id)
          } catch (e) {
            if (this.isTokenExpired(e)) throw e
            // FEEDBACK_NOT_FOUND or transient — keep phase='rating';
            // user can still pick a star, choices may load later.
          }
        } else {
          // Terminal status with no feedback row — closed/resolved banner
          // rather than the active chat input, so the user doesn't see the
          // composer on a ticket the backend already considers done.
          this.phase = statusToPhase(detail.status)
        }
        this.messages = this._hydrateMessages(detail.messages)

        // Resolve presigned URLs for image attachments after hydration
        for (const m of this.messages) {
          for (const att of m.attachments ?? []) {
            if (att.type !== 'image' || att.uri) continue
            try {
              const url = await client.getAttachmentViewUrl(String(att.id))
              this.attachmentViewUrls[String(att.id)] = url
              att.uri = url
            } catch {
              /* skip */
            }
          }
        }
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'load_chat_failed'
      } finally {
        this.loading = false
      }
    },

    // Re-read the open chat from the API and derive the feedback phase from
    // its server-truth flags. Called when a ticket closes (and when the
    // feedback_triggered event fires) so the rating decision never relies on
    // the flaky WS event alone. The rating screen shows ONLY for a closed (5)
    // ticket that requested feedback and hasn't been rated yet; a reopened or
    // resolved ticket, or one already rated, keeps its non-rating phase.
    // Does not touch this.messages — live message events keep those current.
    async syncFeedbackState(): Promise<void> {
      const client = this.client
      if (!client || !this.chatId) return
      try {
        const detail = await client.getChat(this.chatId)
        this.feedbackSubmitted = !!detail.feedbackSubmitted
        // Reopened/resolved in the meantime — leave the live phase alone.
        if (detail.status !== 'closed') return
        if (detail.feedbackSubmitted) {
          // Already rated → thanks panel, never the rating screen.
          this.phase = 'resolved'
        } else if (detail.feedbackRequested) {
          this.phase = 'rating'
          try {
            this.feedbackDetail = await client.getFeedback(this.chatId)
          } catch (e) {
            if (this.isTokenExpired(e)) throw e
            // choices may load later; user can still pick a star
          }
        } else {
          // Closed but no feedback requested → plain "ticket closed" banner.
          this.phase = 'closed'
        }
      } catch (e) {
        if (this.isTokenExpired(e)) this.handleTokenExpired()
      }
    },

    // Ticket listing via SDK ------------------------------------------
    async loadTickets(page = 1, perPage = 25, append = false): Promise<void> {
      const client = this.ensureClient()
      if (!client) return
      this.loading = true
      try {
        const { chats, total, openTotal } = await client.listChats(page, perPage)
        const view = chats
          .map(toViewTicket)
          .sort((a, b) => +new Date(b.last_message_at) - +new Date(a.last_message_at))
        this.tickets = append ? [...this.tickets, ...view] : view
        this.ticketsTotal = total
        // Server-wide open count (same on every page) — stable badge floor.
        this.ticketsOpenTotal = openTotal ?? 0
        this.ticketsPage = page
        this.ticketsPerPage = perPage
        this.ticketsFetched = true
      } catch (e) {
        if (this.isTokenExpired(e)) {
          this.handleTokenExpired()
          return
        }
        this.error = e instanceof Error ? e.message : 'list_tickets_failed'
      } finally {
        this.loading = false
      }
    },

    async endChat(): Promise<void> {
      const client = this.client
      if (!client || !this.chatId) return
      try {
        await client.endChat(this.chatId)
      } catch (e) {
        this.error = e instanceof Error ? e.message : 'end_chat_failed'
      }
      this.phase = 'closed'
    },

    // Messaging --------------------------------------------------------
    async sendMessage(text: string): Promise<void> {
      const client = this.client
      if (!client || !this.chatId) return
      const trimmed = text.trim()
      if (!trimmed) return

      const localId = `local-${Date.now()}`
      const nowSec = Math.floor(Date.now() / 1000)
      this.messages.push({
        id: localId,
        text: trimmed,
        time: formatTime(nowSec),
        timestamp: nowSec,
        type: 'user',
      })

      try {
        const sent = await client.sendMessage(this.chatId, trimmed)
        // Adopt the server id, exactly as sendAttachment already does. An agent
        // holding edit_any / delete_any can act on a customer message, and both
        // WS events key on the server id — a bubble left on its `local-…` id
        // would never match, so the edit or deletion would be invisible here
        // until a reload.
        const target = this.messages.find((m) => m.id === localId)
        if (target) target.id = sent.id
      } catch (e) {
        this.messages = this.messages.filter((m) => m.id !== localId)
        if (this.isTokenExpired(e)) {
          this.handleTokenExpired()
          return
        }
        this.error = e instanceof Error ? e.message : 'send_failed'
      }
    },

    async sendAttachment(file: File): Promise<void> {
      const client = this.client
      if (!client || !this.chatId) return

      const localId = `local-${Date.now()}`
      const nowSec = Math.floor(Date.now() / 1000)
      const isImage = file.type.startsWith('image/')
      const objectUrl = URL.createObjectURL(file)
      this.messages.push({
        id: localId,
        text: '',
        time: formatTime(nowSec),
        timestamp: nowSec,
        type: 'user',
        attachments: [
          {
            id: localId,
            type: isImage ? 'image' : 'file',
            uri: objectUrl,
            fileName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          },
        ],
      })

      try {
        // The SDK requires a messageId to attach to — create a placeholder
        // message whose body is the file name, then upload against it.
        const msg = await client.sendMessage(this.chatId, file.name)
        const attachment = await client.uploadAttachment(msg.id, file)

        // Reconcile the optimistic ids and uri with the server response.
        // Without this, /view-url and /download-url calls keep using the
        // `local-…` placeholder, which gorilla/mux's `[0-9]+` constraint
        // rejects with a 404 (surfaced as a misleading CORS error). Also
        // swap the blob preview for the presigned URL so the attachment
        // survives a session reload, and revoke the now-unused blob.
        const target = this.messages.find((m) => m.id === localId)
        if (target) {
          target.id = msg.id
          const targetAtt = target.attachments?.[0]
          if (targetAtt) {
            targetAtt.id = attachment.id
            if (attachment.downloadUrl) {
              targetAtt.uri = attachment.downloadUrl
              URL.revokeObjectURL(objectUrl)
            }
          }
        }
      } catch (e) {
        this.messages = this.messages.filter((m) => m.id !== localId)
        URL.revokeObjectURL(objectUrl)
        if (this.isTokenExpired(e)) {
          this.handleTokenExpired()
          return
        }
        this.error = e instanceof Error ? e.message : 'upload_failed'
      }
    },

    sendTyping(isTyping: boolean): void {
      if (!this.client || !this.chatId) return
      try {
        this.client.sendTypingIndicator(this.chatId, isTyping)
      } catch {
        /* fire-and-forget */
      }
    },

    // Token-expiry helper ----------------------------------------------
    isTokenExpired(e: unknown): boolean {
      const code = (e as { code?: string } | null)?.code
      const status = (e as { statusCode?: number } | null)?.statusCode
      return code === 'TOKEN_EXPIRED' || status === 401
    },

    handleTokenExpired(): void {
      // Guest sessions expire too — but redirecting an anonymous user
      // to the auth flow is wrong. Drop their state and reopen the
      // guest info form instead.
      const wasGuest = this.guestSessionToken !== null
      const config = useRuntimeConfig()
      this.clearState()
      if (wasGuest) {
        this.phase = 'guest-info'
        this.isPanelOpen = true
        return
      }
      try {
        window.location.href = config.public.authLoginUrl as string
      } catch {}
    },

    // Teardown ---------------------------------------------------------
    disconnect(): void {
      this.unsubs.forEach((off) => off())
      this.unsubs = []
      this.clearTypingFallback()
      try {
        this.client?.disconnect()
      } catch {}
      this.client = null
      // Drop the in-memory chatId so reopening the panel falls into
      // subject-pick. /ticket → loadChat sets chatId in memory before
      // re-opening, so that flow is unaffected.
      this.chatId = null
      this.connectionState = 'disconnected'
      this.prevConnectionState = 'disconnected'
      this.hasInitiallyLoaded = false
      this._subscribedChatId = null
    },

    clearState(): void {
      this.disconnect()
      this.messages = []
      this._deletedByChat = {}
      this._editsByChat = {}
      this.subjects = []
      this._allSubjects = []
      this.feedbackDetail = null
      this.rating = 0
      this.isTyping = false
      this.error = null
      this.loading = false
      this.phase = 'idle'
      this.attachmentViewUrls = {}
      this.isPanelOpen = false
      this.guestSessionToken = null
      this.pendingSubject = null
    },

    // Feedback ---------------------------------------------------------
    // Sets `rating` and lets the UI render the form for any star count
    // (the API exposes per-rating choices for 1..5). feedbackDetail is
    // normally pre-loaded by loadChat or onFeedbackRequested; only
    // fetch here as a fallback, and never null an already-loaded value
    // on a failed retry.
    async submitRating(stars: number): Promise<void> {
      this.rating = stars
      const client = this.client
      if (client && this.chatId && !this.feedbackDetail) {
        try {
          this.feedbackDetail = await client.getFeedback(this.chatId)
        } catch (e) {
          if (this.isTokenExpired(e)) {
            this.handleTokenExpired()
          }
          // Keep existing value (may already be null) — don't clobber
          // a previously-good fetch with a transient failure.
        }
      }
    },

    // Dismiss the feedback form takeover ("Daha sonra" button) — drops
    // back to the inline-stars view inside the same 'rating' phase.
    dismissFeedbackForm(): void {
      this.rating = 0
    },

    // choiceIds are real numeric IDs from the server's choicesByRating
    // (see `feedbackChoices` getter). The form receives the sorted list
    // and emits the selected IDs directly.
    async submitFeedback(choiceIds: number[], comment: string): Promise<void> {
      const client = this.client
      if (!client || !this.chatId) return
      try {
        await client.submitFeedback(this.chatId, this.rating, choiceIds, comment)
        this.phase = 'resolved'
        this.feedbackSubmitted = true
        // Thanks bubble is rendered by PChatContainer when phase==='resolved'
        // (after the inline stars), so loaded-from-server resolved chats
        // also get it without server help.
      } catch (e) {
        if (this.isTokenExpired(e)) {
          this.handleTokenExpired()
          return
        }
        this.error = e instanceof Error ? e.message : 'feedback_failed'
      }
    },

    // Attachment URL helpers (cached) ----------------------------------
    async getAttachmentViewUrl(attachmentId: string): Promise<string | null> {
      if (this.attachmentViewUrls[attachmentId]) return this.attachmentViewUrls[attachmentId]
      const client = this.client
      if (!client) return null
      try {
        const url = await client.getAttachmentViewUrl(attachmentId)
        this.attachmentViewUrls[attachmentId] = url
        return url
      } catch {
        return null
      }
    },

    // Pull an attachment's bytes so the browser can save them.
    //
    // Goes through our own Nitro route rather than straight at
    // helpdesk-core, for two reasons the browser can't work around:
    // /download-url (the one endpoint that presigns with
    // `Content-Disposition: attachment`) is not exposed at the prod edge,
    // and the presigned /view-url that IS exposed points at a bucket
    // origin with no CORS grant for us, so fetching it from here fails
    // before it starts. server/api/attachments/[id]/download.get.ts does
    // both hops server-side, where neither limit applies, and is
    // same-origin to us. It relays this token and nothing else — upstream
    // still authorizes.
    //
    // Returns a Blob rather than a URL because that's what makes the
    // filename work: an object URL built from it is same-origin, the one
    // case where an anchor's `download` attribute actually applies.
    // Mirrors what mobile does with File.downloadFileAsync before handing
    // the file to the OS.
    //
    // A 401 is handled here rather than reported: the route relays the
    // upstream status precisely so an expired session can reach the
    // re-auth path every other action in this store already uses, instead
    // of showing a generic failure to someone who just needs a new token.
    async fetchAttachmentBlob(attachmentId: string): Promise<AttachmentBlobResult> {
      const token = this._resolveAuthToken()
      if (!token) return { blob: null, tokenExpired: false }

      const config = useRuntimeConfig()
      // Server routes sit under app.baseURL ('/destek/'), so build the path
      // off it instead of hardcoding a leading '/api'.
      const base = trimTrailingSlash(config.app?.baseURL ?? '/')

      // Headers get a fixed deadline; the body gets an idle one that every
      // chunk resets (see readBlobWithIdleTimeout). Clearing the deadline
      // outright at this point instead — the obvious way to stop cutting
      // off big files — would let a connection that dies after the headers
      // hang this action forever, and because the caller only clears its
      // loading flag in a `finally`, the download button would spin until
      // a page reload.
      const controller = new AbortController()
      const headerTimer = setTimeout(() => controller.abort(), HEADER_TIMEOUT_MS)
      try {
        const res = await fetch(
          `${base}/api/attachments/${encodeURIComponent(attachmentId)}/download`,
          { headers: { 'X-Customer-Token': token }, signal: controller.signal },
        )
        clearTimeout(headerTimer)

        if (res.status === 401) {
          this.handleTokenExpired()
          return { blob: null, tokenExpired: true }
        }
        if (!res.ok || !res.body) return { blob: null, tokenExpired: false }

        const blob = await readBlobWithIdleTimeout(res, controller)
        return { blob, tokenExpired: false }
      } catch {
        return { blob: null, tokenExpired: false }
      } finally {
        clearTimeout(headerTimer)
      }
    },
  },
})

/**
 * Wire the locale watcher. Call once from a top-level component so that the
 * SDK is reconstructed when the user switches language. Returns a stop handle.
 */
export function watchHelpdeskLocale(): () => void {
  const chat = useHelpdeskChatStore()
  const { locale } = useI18n()
  // Seed the cached locale once we have a setup-context handle on i18n.
  // ensureClient reads it without calling useI18n() so it can be invoked
  // safely from async actions (e.g. connectAsGuest).
  chat.currentLocale = locale.value
  const stop = watch(locale, async (newLocale) => {
    chat.currentLocale = newLocale
    if (!chat.client) return
    chat.disconnect()
    await chat.connect()
  })
  return stop
}
